import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Query key factory
// ============================================================================

const clientActionKeys = {
  all: ['client-actions'] as const,
  project: (projectId: string) => ['client-actions', projectId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useClientActions(projectId: string | undefined) {
  return useQuery({
    queryKey: clientActionKeys.project(projectId!),
    queryFn: () => api.clientActions.list(projectId!),
    enabled: !!projectId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateClientAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: {
        title: string;
        action_type?: string;
        description?: string;
        due_date?: string;
        is_required?: boolean;
        position?: number;
      };
    }) => api.clientActions.create(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientActionKeys.project(variables.projectId) });
      toast.success('Action item added');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to add action item');
    },
  });
}

export function useUpdateClientAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      actionId,
      data,
    }: {
      projectId: string;
      actionId: string;
      data: {
        title?: string;
        action_type?: string;
        description?: string;
        due_date?: string;
        position?: number;
        is_required?: boolean;
      };
    }) => api.clientActions.update(projectId, actionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientActionKeys.project(variables.projectId) });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update action item');
    },
  });
}

export function useDeleteClientAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, actionId }: { projectId: string; actionId: string }) =>
      api.clientActions.delete(projectId, actionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientActionKeys.project(variables.projectId) });
      toast.success('Action item deleted');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete action item');
    },
  });
}
